import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children"],
  _excluded2 = ["scope", "children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
function createContext(rootComponentName, defaultContext) {
  var Context = /*#__PURE__*/React.createContext(defaultContext);
  var Provider = function Provider(props) {
    var children = props.children,
      context = _objectWithoutProperties(props, _excluded);
    // Перемемоизация только при изменении значений пропсов
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var value = React.useMemo(function () {
      return context;
    }, Object.values(context));
    return /*#__PURE__*/_jsx(Context.Provider, {
      value: value,
      children: children
    });
  };
  Provider.displayName = rootComponentName + 'Provider';
  function useContext(consumerName) {
    var context = React.useContext(Context);
    if (context) {
      return context;
    }
    if (defaultContext !== undefined) {
      return defaultContext;
    }
    // если defaultContext не был указан, это обязательный контекст.
    throw new Error("`".concat(consumerName, "` \u0434\u043E\u043B\u0436\u0435\u043D \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u044C\u0441\u044F \u0432\u043D\u0443\u0442\u0440\u0438 `").concat(rootComponentName, "`"));
  }
  return [Provider, useContext];
}

/**
 * Создает контексты для каждого scope
 *
 * Зачем это нужно?
 *
 * Широкое использование compound-компонентов ведет к тому, что в определенный момент (композиция наследуемых компонентов)
 * может произойти пересечение контекстов. Для того чтобы избежать этого, мы можем создать scope для каждого контекста.
 * см. объяснение в https://github.com/radix-ui/primitives/discussions/1091
 */

function createContextScope(scopeName) {
  var createContextScopeDeps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var defaultContexts = [];

  /* -----------------------------------------------------------------------------------------------
   * createContext
   * ---------------------------------------------------------------------------------------------*/

  function createContext(rootComponentName, defaultContext) {
    var BaseContext = /*#__PURE__*/React.createContext(defaultContext);
    var index = defaultContexts.length;
    defaultContexts = [].concat(_toConsumableArray(defaultContexts), [defaultContext]);
    var Provider = function Provider(props) {
      var _scope$scopeName;
      var scope = props.scope,
        children = props.children,
        context = _objectWithoutProperties(props, _excluded2);
      var Context = (scope === null || scope === void 0 || (_scope$scopeName = scope[scopeName]) === null || _scope$scopeName === void 0 ? void 0 : _scope$scopeName[index]) || BaseContext;
      // Перемемоизация только при изменении значений пропсов
      // eslint-disable-next-line react-hooks/exhaustive-deps
      var value = React.useMemo(function () {
        return context;
      }, Object.values(context));
      return /*#__PURE__*/_jsx(Context.Provider, {
        value: value,
        children: children
      });
    };
    Provider.displayName = rootComponentName + 'Provider';
    function useContext(consumerName, scope) {
      var _scope$scopeName2;
      var Context = (scope === null || scope === void 0 || (_scope$scopeName2 = scope[scopeName]) === null || _scope$scopeName2 === void 0 ? void 0 : _scope$scopeName2[index]) || BaseContext;
      var context = React.useContext(Context);
      if (context) {
        return context;
      }
      if (defaultContext !== undefined) {
        return defaultContext;
      }
      // если defaultContext не был указан, это обязательный контекст.
      throw new Error("`".concat(consumerName, "` \u0434\u043E\u043B\u0436\u0435\u043D \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u044C\u0441\u044F \u0432\u043D\u0443\u0442\u0440\u0438 `").concat(rootComponentName, "`"));
    }
    return [Provider, useContext];
  }

  /* -----------------------------------------------------------------------------------------------
   * createScope
   * ---------------------------------------------------------------------------------------------*/

  var createScope = function createScope() {
    var scopeContexts = defaultContexts.map(function (defaultContext) {
      return /*#__PURE__*/React.createContext(defaultContext);
    });
    return function useScope(scope) {
      var contexts = (scope === null || scope === void 0 ? void 0 : scope[scopeName]) || scopeContexts;
      return React.useMemo(function () {
        return _defineProperty({}, "__scope".concat(scopeName), _objectSpread(_objectSpread({}, scope), {}, _defineProperty({}, scopeName, contexts)));
      }, [scope, contexts]);
    };
  };
  createScope.scopeName = scopeName;
  return [createContext, composeContextScopes.apply(void 0, [createScope].concat(_toConsumableArray(createContextScopeDeps)))];
}

/* -------------------------------------------------------------------------------------------------
 * composeContextScopes
 * -----------------------------------------------------------------------------------------------*/

function composeContextScopes() {
  for (var _len = arguments.length, scopes = new Array(_len), _key = 0; _key < _len; _key++) {
    scopes[_key] = arguments[_key];
  }
  var baseScope = scopes[0];
  if (scopes.length === 1) {
    return baseScope;
  }
  var createScope = function createScope() {
    var scopeHooks = scopes.map(function (createScope) {
      return {
        useScope: createScope(),
        scopeName: createScope.scopeName
      };
    });
    return function useComposedScopes(overrideScopes) {
      var nextScopes = scopeHooks.reduce(function (nextScopes, _ref2) {
        var useScope = _ref2.useScope,
          scopeName = _ref2.scopeName;
        // Мы вызываем хук внутри колбэка, что React предупреждает, чтобы избежать несогласованных
        // рендеров, однако, scoping не имеет побочных эффектов рендера, поэтому мы игнорируем правило.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        var scopeProps = useScope(overrideScopes);
        var currentScope = scopeProps["__scope".concat(scopeName)];
        return _objectSpread(_objectSpread({}, nextScopes), currentScope);
      }, {});
      return React.useMemo(function () {
        return _defineProperty({}, "__scope".concat(baseScope.scopeName), nextScopes);
      }, [nextScopes]);
    };
  };
  createScope.scopeName = baseScope.scopeName;
  return createScope;
}
export { createContext, createContextScope };